// Mapa de calor de actividad - VISTA CALENDARIO REAL
// Query directa a Supabase para datos precisos por día/hora

import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { supabase } from '../../../supabaseClient';

/**
 * Mapa de calor de actividad temporal
 * Vista calendario: cada fila es un día real con navegación por semanas
 * Usa query directa a Supabase para datos precisos
 */
export const ActivityHeatmap = ({ buyerSummaries, propertyId }) => {
  // Offset de semanas (0 = semana actual, -1 = semana pasada, etc.)
  const [weekOffset, setWeekOffset] = useState(0);
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calcular rango de fechas de la semana seleccionada
  const weekRange = useMemo(() => {
    const today = new Date();
    const currentDay = today.getDay(); // 0=Dom, 1=Lun, ...
    const daysToMonday = currentDay === 0 ? 6 : currentDay - 1;
    
    // Lunes de la semana actual
    const mondayThisWeek = new Date(today);
    mondayThisWeek.setDate(today.getDate() - daysToMonday);
    mondayThisWeek.setHours(0, 0, 0, 0);
    
    // Aplicar offset de semanas
    const mondayTarget = new Date(mondayThisWeek);
    mondayTarget.setDate(mondayTarget.getDate() + (weekOffset * 7));
    
    // Domingo de esa semana
    const sundayTarget = new Date(mondayTarget);
    sundayTarget.setDate(mondayTarget.getDate() + 6);
    sundayTarget.setHours(23, 59, 59, 999);
    
    return {
      start: mondayTarget,
      end: sundayTarget,
    };
  }, [weekOffset]);

  // Generar los 7 días de la semana con sus fechas
  const weekDays = useMemo(() => {
    const days = [];
    const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekRange.start);
      date.setDate(weekRange.start.getDate() + i);
      
      // Generar fullDate en formato local (no UTC)
      const localYear = date.getFullYear();
      const localMonth = String(date.getMonth() + 1).padStart(2, '0');
      const localDay = String(date.getDate()).padStart(2, '0');

      days.push({
        name: dayNames[i],
        date: date,
        dateStr: `${date.getDate()}/${date.getMonth() + 1}`,
        fullDate: `${localYear}-${localMonth}-${localDay}`,
      });
    }
    
    return days;
  }, [weekRange]);

  // Cargar datos del heatmap desde Supabase
  useEffect(() => {
    if (!propertyId) {
      setLoading(false);
      return;
    }

    const loadHeatmapData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Formatear fechas para la query
        const startDate = weekRange.start.toISOString();
        const endDate = weekRange.end.toISOString();

        // Query: obtener eventos de page_view en el rango
        const { data, error: queryError } = await supabase
          .from('engagement_events')
          .select('created_at')
          .eq('property_id', propertyId)
          .eq('event_type', 'page_view')
          .gte('created_at', startDate)
          .lte('created_at', endDate);

        if (queryError) throw queryError;

        // Procesar eventos y agrupar por día/hora LOCAL
        const heatmap = processEventsToHeatmap(data || [], weekDays);
        setHeatmapData(heatmap);

      } catch (err) {
        console.error('Error cargando heatmap:', err);
        setError('Error al cargar datos de actividad');
      } finally {
        setLoading(false);
      }
    };

    loadHeatmapData();
  }, [propertyId, weekRange, weekDays]);

  // Si no hay propertyId, mostrar mensaje
  if (!propertyId) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400">
          Selecciona una propiedad para ver el mapa de actividad
        </p>
      </div>
    );
  }

  // Verificar si hay datos en general (usando buyerSummaries como indicador)
  if (buyerSummaries.length === 0 || !buyerSummaries.some(b => b.lastVisit)) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400">
          No hay suficientes datos de actividad para mostrar el mapa
        </p>
      </div>
    );
  }

  const totalVisitsThisWeek = heatmapData 
    ? heatmapData.dayStats.reduce((sum, d) => sum + d.total, 0) 
    : 0;

  // Formatear rango para mostrar
  const formatDateRange = () => {
    const start = weekRange.start;
    const end = weekRange.end;
    const startStr = `${start.getDate()} ${getMonthName(start.getMonth())}`;
    const endStr = `${end.getDate()} ${getMonthName(end.getMonth())}`;
    
    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()} - ${endStr}`;
    }
    return `${startStr} - ${endStr}`;
  };

  // Determinar si estamos en la semana actual
  const isCurrentWeek = weekOffset === 0;

  return (
    <div className="space-y-6">
      {/* Navegación de semanas */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setWeekOffset(prev => prev - 1)}
            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="text-sm">Anterior</span>
          </button>

          <div className="text-center">
            <div className="text-lg font-semibold text-white">
              {formatDateRange()}
            </div>
            <div className="text-sm text-slate-400">
              {isCurrentWeek ? 'Esta semana' : 
               weekOffset === -1 ? 'Semana pasada' :
               `Hace ${Math.abs(weekOffset)} semanas`}
            </div>
          </div>

          <button
            onClick={() => setWeekOffset(prev => prev + 1)}
            disabled={isCurrentWeek}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-all ${
              isCurrentWeek 
                ? 'bg-slate-800 text-slate-600 cursor-not-allowed' 
                : 'bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white'
            }`}
          >
            <span className="text-sm">Siguiente</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mb-4"></div>
          <p className="text-slate-400">Cargando datos de actividad...</p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 text-center">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Contenido cuando hay datos */}
      {!loading && !error && heatmapData && (
        <>
          {/* Sin actividad esta semana */}
          {totalVisitsThisWeek === 0 && (
            <div className="bg-slate-700/30 border border-slate-600 rounded-xl p-4 text-center">
              <p className="text-slate-400">
                No hay actividad registrada en esta semana
              </p>
              <button
                onClick={() => setWeekOffset(prev => prev - 1)}
                className="mt-2 text-sm text-amber-400 hover:text-amber-300"
              >
                ← Ver semana anterior
              </button>
            </div>
          )}

          {/* Heatmap compacto */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            {/* Leyenda superior */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Clock className="w-4 h-4" />
                <span>Horas del día</span>
              </div>
              
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Menos</span>
                <div className="flex gap-0.5">
                  {[0, 1, 2, 3, 4, 5].map(intensity => (
                    <div
                      key={intensity}
                      className={`w-3 h-3 rounded-sm ${getHeatColor(intensity)}`}
                    />
                  ))}
                </div>
                <span className="text-slate-500">Más</span>
              </div>
            </div>

            {/* Header de horas */}
            <div className="flex items-center mb-1">
              <div className="w-20 flex-shrink-0" />
              <div className="flex-1 flex justify-between text-xs text-slate-500 px-0.5">
                <span>0h</span>
                <span>6h</span>
                <span>12h</span>
                <span>18h</span>
                <span>24h</span>
              </div>
            </div>

            {/* Grid del heatmap */}
            <div className="space-y-1">
              {heatmapData.days.map((day) => (
                <DayRow key={day.fullDate} day={day} />
              ))}
            </div>

            {/* Estadísticas por día */}
            <div className="mt-6 pt-6 border-t border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-slate-300">
                  Visitas por día
                </h4>
                <span className="text-sm text-slate-400">
                  Total: <span className="text-white font-semibold">{totalVisitsThisWeek}</span>
                </span>
              </div>
              <div className="grid grid-cols-7 gap-2">
                {heatmapData.dayStats.map((stat) => (
                  <div key={stat.fullDate} className="bg-slate-700/50 rounded-lg p-2 text-center">
                    <div className="text-xs text-slate-400 mb-0.5">
                      {stat.name}
                    </div>
                    <div className="text-xs text-slate-500 mb-1">
                      {stat.dateStr}
                    </div>
                    <div className={`text-lg font-bold ${stat.total > 0 ? 'text-white' : 'text-slate-600'}`}>
                      {stat.total}
                    </div>
                    {/* Pico horario */}
                    {stat.peakHour !== null && stat.total > 0 && (
                      <div className="text-xs text-amber-400/70 mt-1">
                        {stat.peakHour}:00-{stat.peakHour + 1}:00
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Fila del día con sus 24 horas
 */
const DayRow = ({ day }) => (
  <div className="flex items-center gap-2">
    {/* Label del día con fecha */}
    <div className="w-20 flex-shrink-0 text-right pr-2">
      <span className="text-xs font-medium text-slate-400">{day.name}</span>
      <span className="text-xs text-slate-500 ml-1">{day.dateStr}</span>
    </div>
    
    {/* Grid de 24 horas */}
    <div 
      className="flex-1 grid gap-0.5"
      style={{ gridTemplateColumns: 'repeat(24, 1fr)' }}
    >
      {day.hours.map((hour) => (
        <HourCell 
          key={hour.hour} 
          hour={hour} 
          dayName={day.name}
          dateStr={day.dateStr}
        />
      ))}
    </div>
  </div>
);

/**
 * Celda individual de hora
 */
const HourCell = ({ hour, dayName, dateStr }) => {
  const colorClass = getHeatColor(hour.intensity);
  
  return (
    <div
      className={`h-4 rounded-sm ${colorClass} cursor-pointer hover:ring-1 hover:ring-amber-400 transition-all relative group`}
    >
      {/* Tooltip on hover */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-20 pointer-events-none">
        <div className="bg-slate-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap border border-slate-700">
          <span className="font-medium">{dayName} {dateStr}</span> · {hour.hour}:00-{hour.hour + 1}:00
          <br />
          <span className="text-amber-400">{hour.count} visita{hour.count !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Obtener color según intensidad (0-5)
 */
const getHeatColor = (intensity) => {
  switch (intensity) {
    case 0: return 'bg-slate-700/50';
    case 1: return 'bg-blue-900/60';
    case 2: return 'bg-blue-600/60';
    case 3: return 'bg-amber-600/60';
    case 4: return 'bg-amber-500/80';
    case 5: return 'bg-amber-400';
    default: return 'bg-slate-700/50';
  }
};

/**
 * Obtener nombre del mes abreviado
 */
const getMonthName = (monthIndex) => {
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return months[monthIndex];
};

/**
 * Procesar eventos raw a estructura de heatmap
 * Convierte timestamps UTC a hora local del navegador
 */
const processEventsToHeatmap = (events, weekDays) => {
  // Inicializar estructura para cada día de la semana
  const heatmap = weekDays.map(day => ({
    ...day,
    hours: Array.from({ length: 24 }, (_, hour) => ({
      hour,
      count: 0,
      intensity: 0,
    })),
  }));

  // Crear mapa de fechas para búsqueda rápida
  const dateToIndex = {};
  weekDays.forEach((day, index) => {
    dateToIndex[day.fullDate] = index;
  });

  // Procesar cada evento
  events.forEach(event => {
    if (!event.created_at) return;

    // Convertir a fecha local del navegador
    const eventDate = new Date(event.created_at);
    
    // Obtener fecha local en formato YYYY-MM-DD
    const localYear = eventDate.getFullYear();
    const localMonth = String(eventDate.getMonth() + 1).padStart(2, '0');
    const localDay = String(eventDate.getDate()).padStart(2, '0');
    const dateStr = `${localYear}-${localMonth}-${localDay}`;
    
    // Obtener hora local
    const hour = eventDate.getHours();

    const dayIndex = dateToIndex[dateStr];

    if (dayIndex !== undefined && heatmap[dayIndex]) {
      heatmap[dayIndex].hours[hour].count++;
    }
  });

  // Calcular intensidad normalizada
  const maxCount = Math.max(
    1,
    ...heatmap.flatMap(day => day.hours.map(h => h.count))
  );

  heatmap.forEach(day => {
    day.hours.forEach(hour => {
      if (hour.count > 0) {
        hour.intensity = Math.ceil((hour.count / maxCount) * 5);
      }
    });
  });

  // Estadísticas por día (con pico horario)
  const dayStats = heatmap.map(day => {
    const total = day.hours.reduce((sum, h) => sum + h.count, 0);

    // Encontrar hora pico del día
    let peakHour = null;
    let peakCount = 0;
    day.hours.forEach(h => {
      if (h.count > peakCount) {
        peakCount = h.count;
        peakHour = h.hour;
      }
    });

    return {
      name: day.name,
      dateStr: day.dateStr,
      fullDate: day.fullDate,
      total,
      peakHour,
      peakCount,
    };
  });

  return {
    days: heatmap,
    dayStats,
  };
};

export default ActivityHeatmap;